import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Archive, ArchiveRestore, Building2, Loader2, Pencil, Plus, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { trade_name: "", abn: "", address: { line1: "", line2: "", city: "", postcode: "", lat: null, lng: null, label: "" }, contact_name: "", contact_phone: "", instructions: "" };
const fmtAbn = (a) => (a ? a.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, "$1 $2 $3 $4") : "—");

function BusinessDialog({ open, onOpenChange, business, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(business ? { ...EMPTY, ...business, address: { ...EMPTY.address, ...(business.address || {}) } } : EMPTY); }, [open, business]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    try {
      if (business) await api.patch(`/businesses/${business.id}`, form);
      else await api.post("/businesses", form);
      toast.success(business ? "Business updated" : "Business added to the directory");
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="business-dialog">
        <DialogHeader>
          <DialogTitle>{business ? "Edit business" : "New business"}</DialogTitle>
          <DialogDescription>Saved businesses auto-fill pickup or delivery details when you build a job.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2"><Label className="text-xs">Trade name</Label><Input value={form.trade_name} onChange={set("trade_name")} data-testid="business-trade-name" className="mt-1 h-9" /></div>
          <div><Label className="text-xs">ABN</Label><Input value={form.abn} onChange={set("abn")} placeholder="11 digits" data-testid="business-abn" className="mt-1 h-9" /></div>
          <div><Label className="text-xs">Delivery contact</Label><Input value={form.contact_name} onChange={set("contact_name")} data-testid="business-contact-name" className="mt-1 h-9" /></div>
          <div><Label className="text-xs">Phone</Label><Input value={form.contact_phone} onChange={set("contact_phone")} placeholder="04xx xxx xxx" data-testid="business-contact-phone" className="mt-1 h-9" /></div>
          <div className="sm:col-span-2">
            <AddressAutocomplete label="Primary address" testid="business-address" value={form.address?.line1 ? { ...form.address, label: form.address.label || `${form.address.line1}, ${form.address.city}` } : null}
              onSelect={(a) => setForm((f) => ({ ...f, address: { line1: a.line1 || "", line2: a.line2 || "", city: a.city || "", postcode: a.postcode || "", lat: a.lat ?? null, lng: a.lng ?? null, label: a.label || "" } }))} />
          </div>
          <div className="sm:col-span-2"><Label className="text-xs">Driver instructions</Label><Textarea value={form.instructions} onChange={set("instructions")} rows={2} placeholder="e.g. Loading dock at rear, buzz twice · closed 12–1pm" data-testid="business-instructions" className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="business-save" className="gap-2 bg-kilted-green hover:bg-kilted-greenLight">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save business</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BusinessesTab() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/businesses", { params: { q, include_archived: showArchived } }).then((r) => setRows(r.data)), [q, showArchived]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const archive = async (b) => {
    try { await api.post(`/businesses/${b.id}/archive`, null, { params: { archived: !b.archived } }); toast.success(b.archived ? "Restored" : "Archived"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Card className="mt-4" data-testid="businesses-tab">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-lg"><Building2 className="h-5 w-5 text-kilted-gold" /> Business directory</CardTitle>
          <p className="text-sm text-muted-foreground">Regular pickup & delivery points — pick one when building a job to auto-fill the address, contact and driver notes.</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="business-add" className="gap-1.5 bg-kilted-green hover:bg-kilted-greenLight"><Plus className="h-4 w-4" /> Add business</Button>
      </CardHeader>
      <CardContent>
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <div className="relative w-72"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ABN, town, contact…" className="h-9 pl-8" data-testid="business-filter" /></div>
          <label className="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="business-show-archived" /> Show archived</label>
        </div>
        {!rows ? <Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /> : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground" data-testid="business-empty">No businesses yet — add your regular pickup and delivery points.</p>
        ) : (
          <Table data-testid="businesses-table">
            <TableHeader><TableRow><TableHead>Business</TableHead><TableHead>ABN</TableHead><TableHead>Address</TableHead><TableHead>Contact</TableHead><TableHead>Driver instructions</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id} data-testid={`business-row-${b.id}`} className={b.archived ? "opacity-50" : ""}>
                  <TableCell className="font-semibold text-kilted-green">{b.trade_name}{b.archived && <span className="ml-2 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">archived</span>}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{fmtAbn(b.abn)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{b.address.line1}, {b.address.city} {b.address.postcode}</TableCell>
                  <TableCell className="text-xs">{b.contact_name || "—"}{b.contact_phone && <div className="text-muted-foreground">{b.contact_phone}</div>}</TableCell>
                  <TableCell className="max-w-[260px] text-xs text-muted-foreground">{b.instructions || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button size="sm" variant="ghost" onClick={() => { setEditing(b); setOpen(true); }} data-testid={`business-edit-${b.id}`} className="h-8 gap-1 text-xs"><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => archive(b)} data-testid={`business-archive-${b.id}`} className="h-8 gap-1 text-xs text-muted-foreground">{b.archived ? <><ArchiveRestore className="h-3.5 w-3.5" /> Restore</> : <><Archive className="h-3.5 w-3.5" /> Archive</>}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <BusinessDialog open={open} onOpenChange={setOpen} business={editing} onSaved={load} />
    </Card>
  );
}
